import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import {
  Users, ArrowRight, Ship, Anchor,
  Check, User, Mail, Phone, Crown, Percent,
  Lock, ShieldCheck, UserMinus, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { earlyBirdInfo, formatDeadline } from '../lib/earlyBird';
import CabinScarcity from '../components/CabinScarcity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

const cruiseImages = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_170fb96af2764aecb7eb7c526a48eb27~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20le%20tour%20de%20Corse%20sognudimare.png',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_2c02fe160efb49b6930f0c695a53e34f~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20corse%20du%20sud%20sognudimare.png',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_bdc5406402ea4be3b94eeeb747d2da1a~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20ouest%20corse%20sognudimare.png',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_68a8fb4c934c44cb909dfc0075f36d83~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20sardaigne%20et%20la%20corse%20du%20sud%20sognudimare.png',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_95912f428d264151b75bce60481c048d~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20gr%C3%A8ce%20authentique%20sognudimare.png',
  'croisiere-catamaran-grece-cyclades-2-semaines': '/images/carte_embarquement_cyclades.png',
  'croisiere-catamaran-grenadines-caraibes': 'https://static.wixstatic.com/media/ce6ce7_748110d102c448599459ce93b281fdb6~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20les%20iles%20grenadines%20sognudimare.png',
};

// Smart resolver — falls back to the cruise's own hero/images if no thumbnail mapping exists.
const getCruiseImage = (cruise) => {
  if (!cruise) return '';
  const mapped = cruiseImages[cruise.slug];
  if (mapped) return mapped;
  return cruise.card_image || cruise.hero_image || (cruise.images && cruise.images[0]) || '/images/hero_lagoon38_sailing.jpg';
};

const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startStr} → ${endStr}`;
};

const ReservationPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedCruiseSlug = searchParams.get('cruise');
  const preselectedDate = searchParams.get('date');

  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCruise, setSelectedCruise] = useState(null);
  const [bookingType, setBookingType] = useState('cabin');
  const [selectedDate, setSelectedDate] = useState('');
  const [membershipEmail, setMembershipEmail] = useState('');
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [step, setStep] = useState('form');
  const [bookingId, setBookingId] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isSolo, setIsSolo] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    cabin_count: 1,
    special_requests: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cruisesRes = await axios.get(`${API}/cruises`);
        setCruises(cruisesRes.data);

        // Pre-select cruise from URL
        if (preselectedCruiseSlug) {
          const found = cruisesRes.data.find(c => c.slug === preselectedCruiseSlug);
          if (found) {
            setSelectedCruise(found);
            if (!found.is_cabin_booking) setBookingType('privatization');
            if (preselectedDate) setSelectedDate(preselectedDate);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [preselectedCruiseSlug, preselectedDate]);

  const handleCruiseSelect = (cruiseId) => {
    const cruise = cruises.find(c => c.id === cruiseId);
    setSelectedCruise(cruise);
    setSelectedDate('');
    if (cruise && !cruise.is_cabin_booking) setBookingType('privatization');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const checkMembership = async () => {
    if (!membershipEmail) return;
    setCheckingMembership(true);
    try {
      const response = await axios.get(`${API}/club-memberships/check/${membershipEmail}`);
      setMembershipStatus(response.data);
      if (response.data.is_member) {
        toast.success(`Membre Club trouve ! ${response.data.membership.discount_percent}% de remise appliquee.`);
      } else {
        toast.info('Aucun abonnement Club actif trouve pour cet email.');
      }
    } catch (error) {
      setMembershipStatus({ is_member: false });
    } finally {
      setCheckingMembership(false);
    }
  };

  // Passengers per cabin
  const passengersPerCabin = isSolo ? 1 : 2;
  const totalPassengers = bookingType === 'cabin' ? formData.cabin_count * passengersPerCabin : (selectedCruise?.max_passengers || 8);

  const getDiscountPercent = useCallback(() => {
    if (membershipStatus?.is_member) return membershipStatus.membership.discount_percent;
    return 0;
  }, [membershipStatus]);

  const calculateTotal = useCallback(() => {
    if (!selectedCruise) return { subtotal: 0, discount: 0, total: 0, deposit: 0, soloSurcharge: 0, boardFee: 0, earlyBirdEligible: false, earlyBirdAmount: 0 };
    let subtotal = 0;
    let soloSurcharge = 0;

    // Use date-specific price if a date is selected, otherwise fall back to base_price
    const selectedDeparture = selectedDate
      ? selectedCruise.dates?.find(d => d.start_date === selectedDate)
      : null;

    if (bookingType === 'cabin') {
      const pricePerPerson = selectedDeparture?.price_per_cabin || selectedCruise.base_price;
      const cabinPrice = pricePerPerson * 2; // prix cabine = 2 passagers
      subtotal = cabinPrice * formData.cabin_count;

      if (isSolo) {
        soloSurcharge = 0;
        subtotal = (cabinPrice * 0.9) * formData.cabin_count; // -10% on cabin price for solo
      }
    } else {
      // Privatisation : si la date a un prix explicite, on l'utilise, sinon on calcule
      // le prix par cabine × nombre de cabines (= prix/pers × max_passengers, car
      // `price_per_cabin` stocke en réalité le prix PAR PERSONNE).
      const datePricePerPerson = selectedDeparture?.price_per_cabin;
      const maxPax = selectedCruise.max_passengers || 8;
      if (selectedDeparture?.privatization_price) {
        subtotal = selectedDeparture.privatization_price;
      } else if (datePricePerPerson) {
        subtotal = datePricePerPerson * maxPax;
      } else {
        subtotal = selectedCruise.privatization_base_price || 0;
      }
    }

    // Caisse de bord: 240€/passager/semaine, 480€/passager/2 semaines
    const isTwoWeeks = selectedCruise.duration?.includes('14');
    const boardFeePerPerson = isTwoWeeks ? 480 : 240;
    const nbPassengers = bookingType === 'cabin' ? formData.cabin_count * passengersPerCabin : (selectedCruise.max_passengers || 8);
    const boardFee = boardFeePerPerson * nbPassengers;

    const discountPercent = getDiscountPercent();
    const discount = subtotal * (discountPercent / 100);

    // Early-booking -5% on subtotal (after club discount, before board fee)
    const eb = selectedDate ? earlyBirdInfo(selectedDate) : { eligible: false };
    const earlyBirdEligible = eb.eligible;
    const earlyBirdAmount = earlyBirdEligible ? Math.round((subtotal - discount) * 0.05) : 0;

    const total = subtotal - discount - earlyBirdAmount + boardFee;
    const deposit = Math.round(total * 0.4);
    const secondPayment = Math.round(total * 0.3);
    const balance = total - deposit - secondPayment;
    return { subtotal, discount, discountPercent, total, deposit, secondPayment, balance, soloSurcharge, boardFee, boardFeePerPerson, nbPassengers, earlyBirdEligible, earlyBirdAmount, earlyBirdDeadline: eb.deadline };
  }, [selectedCruise, selectedDate, bookingType, formData.cabin_count, membershipStatus, isSolo, getDiscountPercent, passengersPerCabin]);

  const totals = calculateTotal();

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!selectedCruise) { toast.error('Veuillez selectionner une croisière'); return; }
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error('Veuillez remplir tous les champs obligatoires'); return;
    }
    if (!cgvAccepted) {
      toast.error('Vous devez accepter les Conditions Générales de Vente avant de procéder au paiement'); return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        cruise_id: selectedCruise.id,
        cruise_name: selectedCruise.name,
        booking_type: bookingType,
        start_date: selectedDate || 'A definir',
        end_date: selectedDate || 'A definir',
        passengers: [],
        cabin_count: bookingType === 'cabin' ? parseInt(formData.cabin_count) : selectedCruise.cabin_count,
        total_amount: totals.total,
        deposit_amount: totals.deposit,
        early_bird_applied: !!totals.earlyBirdEligible,
        early_bird_percent: totals.earlyBirdEligible ? 5 : 0,
        discount_amount: totals.earlyBirdAmount || 0,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        special_requests: formData.special_requests
          + (isSolo ? ' | Cabine solo' : '')
          + (membershipStatus?.is_member ? ` | Membre Club: ${membershipStatus.membership.card_name} (-${membershipStatus.membership.discount_percent}%)` : '')
          + (totals.earlyBirdEligible ? ` | Tarif Anticipé -5% (-${totals.earlyBirdAmount}€)` : ''),
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingId(response.data.id);
      setStep('payment');
      toast.success('Réservation créée ! Procedez au paiement de l\'acompte.');
    } catch (error) {
      toast.error('Erreur lors de la réservation', {
        description: error.response?.data?.detail || 'Veuillez reessayer',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentToken = async (token) => {
    if (!bookingId) return;
    setPaymentProcessing(true);
    try {
      const depositCents = Math.round(totals.deposit * 100);
      const response = await axios.post(`${API}/payments/process`, {
        source_id: token.token,
        booking_id: bookingId,
        amount: depositCents,
        customer_email: formData.customer_email,
        verification_token: token.verificationToken || null,
      });
      if (response.data.success) {
        setPaymentSuccess(true);
        toast.success('Paiement accepte !');
      }
    } catch (error) {
      toast.error('Echec du paiement', { description: error.response?.data?.detail || 'Erreur de paiement' });
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Payment success view
  if (paymentSuccess) {
    return (
      <Layout>
        <section className="pt-32 pb-32 bg-[#F8F9FA] min-h-screen" data-testid="payment-success">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-24 h-24 bg-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="h-12 w-12 text-white" />
            </div>
            <h1 className="font-display text-4xl font-bold text-[#0A2540] mb-4">Réservation confirmée !</h1>
            <p className="text-[#4A5568] text-lg mb-8">
              Votre acompte de <strong>{totals.deposit}€</strong> a ete debite avec succes.
              Vous recevrez un email de confirmation a <strong>{formData.customer_email}</strong>.
            </p>
            <Card className="text-left mb-8">
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between"><span className="text-[#4A5568]">Croisière</span><span className="font-medium text-[#0A2540]">{selectedCruise?.name}</span></div>
                <div className="flex justify-between"><span className="text-[#4A5568]">Type</span><span className="font-medium text-[#0A2540]">{bookingType === 'cabin' ? `${formData.cabin_count} cabine(s)${isSolo ? ' solo' : ''}` : 'Privatisation'}</span></div>
                <div className="flex justify-between"><span className="text-[#4A5568]">Caisse de bord</span><span className="font-medium text-[#0A2540]">{totals.boardFee}€</span></div>
                <div className="flex justify-between"><span className="text-[#4A5568]">Total</span><span className="font-bold text-[#0A2540]">{totals.total.toFixed(0)}€</span></div>
                <div className="flex justify-between border-t pt-3"><span className="text-[#4A5568]">Acompte paye (40%)</span><span className="font-bold text-[#2A9D8F]">{totals.deposit}€</span></div>
                <div className="flex justify-between"><span className="text-[#4A5568]">2e paiement (30%) - J+30</span><span className="font-medium text-[#0A2540]">{totals.secondPayment}€</span></div>
                <div className="flex justify-between"><span className="text-[#4A5568]">Solde - 60j avant départ</span><span className="font-medium text-[#C4724F]">{totals.balance}€</span></div>
              </CardContent>
            </Card>
            <p className="text-sm text-[#94A3B8] mb-6">
              2e versement de {totals.secondPayment}€ dans 30 jours.
              Solde de {totals.balance}€ a regler 60 jours avant l'embarquement.
            </p>
            <Button asChild className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white">
              <Link to="/">Retour a l'accueil</Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  // Available dates for selected cruise
  const availableDates = selectedCruise?.dates
    ?.filter(d => d.status !== 'full' && new Date(d.start_date) > new Date())
    ?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date)) || [];

  return (
    <Layout>
      <SEO
        title="Réserver votre croisière catamaran — Cabine ou privatisation"
        description="Réservez en ligne votre croisière catamaran SOGNUDIMARE en Corse, Sardaigne, Méditerranée. Cabine double dès 1 290 € ou privatisation Lagoon 46/43/38. Paiement sécurisé, pension complète, équipage professionnel."
        path="/reservation-cabines-privatisation-catamaran-sognudimare"
        keywords={[
          'réserver croisière catamaran',
          'réservation catamaran corse',
          'cabine catamaran ajaccio',
          'cabine catamaran porto-vecchio',
          'cabine catamaran calvi',
          'privatisation catamaran corse',
          'privatiser un catamaran',
          'location catamaran avec skipper corse',
          'paiement sécurisé catamaran',
          'lagoon 46 réservation',
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ReservationPackage',
          name: 'Réservation croisière catamaran SOGNUDIMARE',
          provider: { '@type': 'TravelAgency', name: 'SOGNUDIMARE', url: 'https://sognudimare.com' },
        }}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#0A2540]" data-testid="reservation-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
            <Anchor className="h-4 w-4 text-[#2A9D8F]" />
            <span className="text-white/80 text-sm">Réservation en ligne</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
            Réservez votre <span className="italic text-[#2A9D8F]">croisière</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Choisissez votre destination, votre type de réservation et profitez de remises exclusives avec le Club des Voyageurs.
          </p>
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'form' ? 'bg-[#2A9D8F] text-white' : 'bg-white/10 text-white/60'}`}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">1</span>
              Votre croisière
            </div>
            <ArrowRight className="h-4 w-4 text-white/40" />
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'payment' ? 'bg-[#2A9D8F] text-white' : 'bg-white/10 text-white/60'}`}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">2</span>
              Paiement sécurisé
            </div>
          </div>
        </div>
      </section>

      {/* STEP 1: Booking Form */}
      {step === 'form' && (
        <section className="py-16 bg-[#FDFCFA]" data-testid="reservation-form">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <form onSubmit={handleSubmitForm}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  {/* Step 1: Choose Cruise */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#2A9D8F] rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                        Choisissez votre croisière
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {cruises.filter(c => !c.is_on_request).map((cruise) => (
                          <div
                            key={cruise.id}
                            onClick={() => handleCruiseSelect(cruise.id)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedCruise?.id === cruise.id ? 'border-[#2A9D8F] bg-[#2A9D8F]/5' : 'border-slate-200 hover:border-[#2A9D8F]/50'
                            }`}
                            data-testid={`cruise-option-${cruise.slug}`}
                          >
                            <div className="flex gap-3">
                              <img src={getCruiseImage(cruise)} alt={cruise.name} className="w-16 h-12 object-cover rounded" />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-[#0A2540] text-sm truncate">{cruise.name}</h4>
                                <p className="text-xs text-[#4A5568]">{cruise.duration}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Date selection */}
                      {selectedCruise && availableDates.length > 0 && (
                        <div className="mt-6">
                          <Label className="text-sm font-semibold text-[#0A2540] mb-2 block">Date de départ</Label>
                          <Select value={selectedDate} onValueChange={setSelectedDate}>
                            <SelectTrigger className={`mt-1 h-12 text-base border-2 ${selectedDate ? 'border-[#2A9D8F] bg-[#2A9D8F]/5' : 'border-[#C4724F] bg-[#C4724F]/5 animate-pulse'}`} data-testid="date-select-trigger">
                              <SelectValue placeholder="Choisir une date de départ" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableDates.map((d) => (
                                <SelectItem key={d.start_date} value={d.start_date}>
                                  {formatDateRange(d.start_date, d.end_date)} — {d.price_per_cabin}€/pers
                                  {d.available_cabins !== undefined && d.available_cabins <= 2 ? ` · ${d.available_cabins === 1 ? 'Dernière cabine !' : 'Plus que 2 cabines'}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!selectedDate && (
                            <p className="text-xs text-[#C4724F] mt-1 font-medium">Sélectionnez une date pour voir le prix exact</p>
                          )}
                          {/* Cabin availability indicator (shown for cabin bookings only) */}
                          {selectedDate && bookingType === 'cabin' && (() => {
                            const sel = availableDates.find((d) => d.start_date === selectedDate);
                            if (!sel || sel.available_cabins === undefined) return null;
                            return (
                              <div className="mt-3" data-testid="reservation-cabin-scarcity">
                                <CabinScarcity available={sel.available_cabins} variant="large" />
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 2: Booking Type */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#2A9D8F] rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
                        Type de réservation
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Tabs value={bookingType} onValueChange={(v) => { setBookingType(v); if (v === 'privatization') setIsSolo(false); }} className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="cabin" disabled={selectedCruise && !selectedCruise.is_cabin_booking} data-testid="cabin-tab">
                            <Users className="h-4 w-4 mr-2" /> Réservation cabine
                          </TabsTrigger>
                          <TabsTrigger value="privatization" data-testid="privatization-tab">
                            <Ship className="h-4 w-4 mr-2" /> Privatisation
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="cabin" className="mt-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="cabin_count">Nombre de cabines</Label>
                              <Select value={formData.cabin_count.toString()} onValueChange={(v) => setFormData(prev => ({ ...prev, cabin_count: parseInt(v) }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[1, 2, 3, 4].map((num) => (
                                    <SelectItem key={num} value={num.toString()}>
                                      {num} cabine{num > 1 ? 's' : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Passagers par cabine</Label>
                              <div className="flex gap-2 mt-1">
                                <button
                                  type="button"
                                  onClick={() => setIsSolo(false)}
                                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                    !isSolo ? 'border-[#2A9D8F] bg-[#2A9D8F]/5 text-[#2A9D8F]' : 'border-slate-200 text-[#4A5568] hover:border-slate-300'
                                  }`}
                                  data-testid="duo-btn"
                                >
                                  <Users className="h-4 w-4" /> 2 pers.
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setIsSolo(true)}
                                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                    isSolo ? 'border-[#C4724F] bg-[#C4724F]/5 text-[#C4724F]' : 'border-slate-200 text-[#4A5568] hover:border-slate-300'
                                  }`}
                                  data-testid="solo-btn"
                                >
                                  <UserMinus className="h-4 w-4" /> Solo
                                </button>
                              </div>
                            </div>
                          </div>
                          {isSolo && (
                            <div className="p-3 bg-[#C4724F]/10 rounded-lg border border-[#C4724F]/20 flex items-start gap-2">
                              <UserMinus className="h-4 w-4 text-[#C4724F] mt-0.5 shrink-0" />
                              <p className="text-sm text-[#C4724F]">
                                <strong>Cabine solo :</strong> Remise de 10% sur le prix de la cabine.
                                Vous profitez de la cabine complète pour vous seul.
                              </p>
                            </div>
                          )}
                          {selectedCruise && (
                            <div className="p-3 bg-[#F5F3EF] rounded-lg">
                              <p className="text-sm text-[#4A5568]">
                                Prix cabine : <span className="font-semibold text-[#0A2540]">{
                                  selectedDate
                                    ? (selectedCruise.dates?.find(d => d.start_date === selectedDate)?.price_per_cabin || selectedCruise.base_price)
                                    : selectedCruise.base_price
                                }€</span> par personne
                                {isSolo && <span className="text-[#C4724F]"> (-10% solo)</span>}
                                {' | '}{formData.cabin_count} cabine{formData.cabin_count > 1 ? 's' : ''}
                                {' = '}<span className="font-semibold text-[#0A2540]">{totalPassengers} passager{totalPassengers > 1 ? 's' : ''}</span>
                              </p>
                            </div>
                          )}
                        </TabsContent>
                        <TabsContent value="privatization" className="mt-4">
                          <div className="p-4 bg-[#0A2540] rounded-lg text-white">
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <Crown className="h-4 w-4 text-[#C4724F]" /> Privatisation complète
                            </h4>
                            <p className="text-sm text-white/70 mb-2">Le catamaran entier pour votre groupe (jusqu'à 8 personnes).</p>
                            {selectedCruise && (() => {
                              const dep = selectedDate ? selectedCruise.dates?.find(d => d.start_date === selectedDate) : null;
                              const maxPax = selectedCruise.max_passengers || 8;
                              const computedPriv = dep?.privatization_price
                                || (dep?.price_per_cabin ? dep.price_per_cabin * maxPax : null)
                                || selectedCruise.privatization_base_price
                                || 0;
                              return (
                                <>
                                  <p className="text-[#2A9D8F] font-display text-2xl font-bold" data-testid="privatization-price">
                                    {Number(computedPriv).toLocaleString('fr-FR')}€
                                  </p>
                                  {!dep && (
                                    <p className="text-xs text-white/50 mt-1">Sélectionnez une date pour voir le tarif exact.</p>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>

                  {/* Step 3: Club Card - simplified */}
                  <Card className="border-[#C4724F]/30 bg-gradient-to-br from-white to-[#C4724F]/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#C4724F] rounded-full flex items-center justify-center text-white text-sm font-bold">3</div>
                        <Crown className="h-5 w-5 text-[#C4724F]" />
                        Club des Voyageurs
                        <Badge className="ml-2 bg-[#C4724F]">Jusqu'à -20%</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Check existing membership */}
                      <div className="p-3 bg-white rounded-lg border border-[#C4724F]/20">
                        <Label className="text-xs mb-1.5 block">Déjà membre ? Vérifiez votre email</Label>
                        <div className="flex gap-2">
                          <Input type="email" placeholder="Votre email d'adhésion" value={membershipEmail} onChange={(e) => setMembershipEmail(e.target.value)} className="flex-1 h-9 text-sm" data-testid="membership-email-input" />
                          <Button type="button" onClick={checkMembership} disabled={checkingMembership} variant="outline" size="sm" className="border-[#C4724F] text-[#C4724F]" data-testid="check-membership-btn">
                            {checkingMembership ? '...' : 'Vérifier'}
                          </Button>
                        </div>
                        {membershipStatus?.is_member && (
                          <div className="mt-2 p-2 bg-[#2A9D8F]/10 rounded flex items-center gap-2">
                            <Check className="h-4 w-4 text-[#2A9D8F]" />
                            <span className="text-xs text-[#2A9D8F] font-medium">
                              Membre {membershipStatus.membership.card_name} - {membershipStatus.membership.discount_percent}% de remise
                              {membershipStatus.active_cards_count > 0 && ` pour ${membershipStatus.active_cards_count} passager${membershipStatus.active_cards_count > 1 ? 's' : ''}`}
                              {membershipStatus.membership.end_date && ` (valable jusqu'au ${new Date(membershipStatus.membership.end_date).toLocaleDateString('fr-FR')})`}
                            </span>
                          </div>
                        )}
                        {membershipStatus && !membershipStatus.is_member && (
                          <div className="mt-2 p-2 bg-[#C4724F]/10 rounded flex items-center gap-2">
                            <Crown className="h-4 w-4 text-[#C4724F]" />
                            <span className="text-xs text-[#C4724F]">
                              Aucun abonnement actif. Adhérez pour bénéficier de remises !
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Link to Club page */}
                      {!membershipStatus?.is_member && (
                        <div className="flex items-center gap-3 p-4 bg-[#F5F3EF] rounded-lg">
                          <img
                            src="https://static.wixstatic.com/media/ce6ce7_ab2c364fbfdd4f3d8aeb847f8cab900f~mv2.png/v1/crop/x_0,y_203,w_1080,h_944/fill/w_120,h_105,fp_0.50_0.50,q_90,enc_avif,quality_auto/cartes%20CLUB%20SOGNUDIMARE%20(1).png"
                            alt="Cartes Club"
                            className="w-20 shrink-0 rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-[#0A2540]">Économisez jusqu'à 20%</p>
                            <p className="text-xs text-[#4A5568] mb-2">Adhérez au Club des Voyageurs et bénéficiez de remises sur toutes vos croisières.</p>
                            <Button asChild size="sm" className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white text-xs" data-testid="join-club-link">
                              <Link to="/carte-club-des-voyageurs">
                                Adhérer au Club <ArrowRight className="ml-1 h-3 w-3" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 4: Contact Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#2A9D8F] rounded-full flex items-center justify-center text-white text-sm font-bold">4</div>
                        Vos coordonnees
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="customer_name">Nom complet *</Label>
                          <div className="relative mt-1">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                            <Input id="customer_name" name="customer_name" value={formData.customer_name} onChange={handleInputChange} placeholder="Jean Dupont" className="pl-10" required data-testid="customer-name-input" />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="customer_phone">Telephone *</Label>
                          <div className="relative mt-1">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                            <Input id="customer_phone" name="customer_phone" value={formData.customer_phone} onChange={handleInputChange} placeholder="+33 6 12 34 56 78" className="pl-10" required data-testid="customer-phone-input" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="customer_email">Email *</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                          <Input id="customer_email" name="customer_email" type="email" value={formData.customer_email} onChange={handleInputChange} placeholder="jean@exemple.com" className="pl-10" required data-testid="customer-email-input" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="special_requests">Demandes particulieres</Label>
                        <Textarea id="special_requests" name="special_requests" value={formData.special_requests} onChange={handleInputChange} placeholder="Allergies alimentaires, demandes speciales..." className="min-h-[80px]" data-testid="special-requests-input" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Summary */}
                <div className="lg:col-span-1">
                  <Card className="sticky top-24 border-2 border-[#2A9D8F]/20">
                    <CardHeader className="bg-[#0A2540] text-white rounded-t-lg">
                      <CardTitle className="text-lg">Recapitulatif</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                      {selectedCruise ? (
                        <>
                          <div className="flex gap-3 p-3 bg-[#F5F3EF] rounded-lg">
                            <img src={getCruiseImage(selectedCruise)} alt={selectedCruise.name} className="w-16 h-12 object-cover rounded" />
                            <div>
                              <h4 className="font-semibold text-[#0A2540] text-sm">{selectedCruise.name}</h4>
                              <p className="text-xs text-[#4A5568]">{selectedCruise.duration}</p>
                            </div>
                          </div>
                          {selectedDate && (
                            <div className="text-xs text-[#2A9D8F] font-medium">
                              {formatDateRange(selectedDate, availableDates.find(d => d.start_date === selectedDate)?.end_date || selectedDate)}
                            </div>
                          )}
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-[#4A5568]">Type</span>
                              <span className="font-medium text-[#0A2540]">
                                {bookingType === 'cabin'
                                  ? `${formData.cabin_count} cab. x ${passengersPerCabin} pers.${isSolo ? ' (solo)' : ''}`
                                  : 'Privatisation'}
                              </span>
                            </div>
                            {bookingType === 'cabin' && selectedDate && (
                              <div className="flex justify-between">
                                <span className="text-[#4A5568]">Prix/personne</span>
                                <span className="font-medium text-[#0A2540]">
                                  {(selectedCruise.dates?.find(d => d.start_date === selectedDate)?.price_per_cabin || selectedCruise.base_price)}€
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-[#4A5568]">Sous-total</span>
                              <span className="font-medium text-[#0A2540]">{totals.subtotal.toFixed(0)}€</span>
                            </div>
                            {isSolo && (
                              <div className="flex justify-between text-[#C4724F]">
                                <span className="flex items-center gap-1 text-xs"><UserMinus className="h-3 w-3" />Remise solo (-10%)</span>
                                <span className="font-medium text-xs">incluse</span>
                              </div>
                            )}
                            {totals.discountPercent > 0 && (
                              <div className="flex justify-between text-[#2A9D8F]">
                                <span className="flex items-center gap-1"><Percent className="h-3 w-3" />Remise Club (-{totals.discountPercent}%)</span>
                                <span className="font-medium">-{totals.discount.toFixed(0)}€</span>
                              </div>
                            )}
                            {totals.earlyBirdEligible && (
                              <div className="flex justify-between items-center text-[#C4724F] bg-[#FFF4EC] -mx-2 px-2 py-1.5 rounded-md border border-[#C4724F]/20">
                                <span className="flex items-center gap-1.5 text-sm font-semibold">
                                  <Sparkles className="h-3.5 w-3.5" />
                                  Tarif Anticipé (-5%)
                                </span>
                                <span className="font-bold">-{totals.earlyBirdAmount}€</span>
                              </div>
                            )}
                            {totals.boardFee > 0 && (
                              <div className="flex justify-between text-[#0A2540]">
                                <span className="text-[#4A5568]">Caisse de bord ({totals.boardFeePerPerson}€ x {totals.nbPassengers} pass.)</span>
                                <span className="font-medium">{totals.boardFee}€</span>
                              </div>
                            )}
                          </div>
                          <div className="border-t pt-4">
                            <div className="flex justify-between items-center">
                              <span className="text-[#4A5568] font-medium">Total</span>
                              <span className="text-3xl font-display font-bold text-[#2A9D8F]">{totals.total.toFixed(0)}€</span>
                            </div>
                            {(totals.discount > 0 || totals.earlyBirdAmount > 0) && (
                              <p className="text-xs text-[#2A9D8F] text-right mt-1">
                                Vous économisez {(totals.discount + totals.earlyBirdAmount).toFixed(0)}€ !
                              </p>
                            )}
                            {totals.earlyBirdEligible && totals.earlyBirdDeadline && (
                              <p className="text-[10px] text-[#C4724F] text-right mt-1">
                                Offre valable jusqu'au {formatDeadline(totals.earlyBirdDeadline)}
                              </p>
                            )}
                          </div>
                          <div className="p-3 bg-[#C4724F]/10 rounded-lg border border-[#C4724F]/20">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-[#C4724F]">Acompte (40%)</span>
                              <span className="text-xl font-display font-bold text-[#C4724F]">{totals.deposit}€</span>
                            </div>
                            <p className="text-xs text-[#4A5568] mt-1">A payer maintenant par carte bancaire</p>
                          </div>
                          <div className="p-3 bg-[#F5F3EF] rounded-lg text-xs text-[#4A5568] space-y-1">
                            <p className="font-medium text-[#0A2540] text-sm mb-1">Échéancier de paiement</p>
                            <div className="flex justify-between"><span>1. Acompte (40%) - aujourd'hui</span><span className="font-semibold text-[#C4724F]">{totals.deposit}€</span></div>
                            <div className="flex justify-between"><span>2. 2e versement (30%) - 30 jours après</span><span className="font-semibold text-[#0A2540]">{totals.secondPayment}€</span></div>
                            <div className="flex justify-between"><span>3. Solde (30%) - 60j avant embarquement</span><span className="font-semibold text-[#0A2540]">{totals.balance}€</span></div>
                          </div>
                          {/* 1% Socio-écologique */}
                          <div
                            className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-br from-[#2A9D8F] via-[#0F766E] to-[#0A2540] border-2 border-[#2A9D8F]/40 shadow-lg shadow-[#2A9D8F]/10"
                            data-testid="biodiversity-contribution"
                          >
                            {/* decorative elements */}
                            <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#C4956A]/20 rounded-full blur-2xl pointer-events-none" />

                            <div className="relative">
                              {/* Header */}
                              <div className="flex items-center gap-2.5 mb-3">
                                <div className="shrink-0 w-10 h-10 rounded-xl bg-white text-[#2A9D8F] flex items-center justify-center shadow-md">
                                  <span className="font-display text-base font-bold leading-none">1%</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold">
                                    Engagement durable
                                  </div>
                                  <div className="font-display text-base sm:text-lg font-bold text-white leading-tight">
                                    Votre impact
                                    <br />
                                    socio-écologique
                                  </div>
                                </div>
                              </div>

                              {/* Big amount */}
                              <div className="flex items-baseline gap-2 mb-3 pb-3 border-b border-white/15">
                                <span className="font-display text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none">
                                  {Math.round(totals.total * 0.01)}€
                                </span>
                                <span className="text-xs sm:text-sm text-white/85 leading-tight">
                                  reversés grâce à votre réservation
                                </span>
                              </div>

                              {/* Beneficiaries */}
                              <p className="text-xs sm:text-[13px] text-white/85 leading-relaxed mb-2.5">
                                Aux associations <strong className="text-white">Mare Vivu</strong> et{' '}
                                <strong className="text-white">Tous Pour Chacun</strong> pour la
                                préservation de la Méditerranée et l'inclusion sociale.
                              </p>

                              {/* Mini chips */}
                              <div className="flex flex-wrap gap-1.5">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-[10px] font-semibold text-white">
                                  Méditerranée
                                </span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-[10px] font-semibold text-white">
                                  Biodiversité
                                </span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-[10px] font-semibold text-white">
                                  Inclusion
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-[#94A3B8] text-sm text-center py-8">Sélectionnez une croisière pour voir le recapitulatif</p>
                      )}

                      {/* CGV acceptance — REQUIRED to enable payment */}
                      <label className="flex items-start gap-2.5 text-xs text-[#94A3B8] leading-relaxed cursor-pointer select-none p-3 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors" data-testid="cgv-checkbox-label">
                        <input
                          type="checkbox"
                          checked={cgvAccepted}
                          onChange={e => setCgvAccepted(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-[#C4724F] shrink-0 cursor-pointer"
                          data-testid="cgv-checkbox"
                        />
                        <span>
                          J'ai lu et j'accepte les{' '}
                          <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="text-[#C4724F] hover:text-[#D4956F] font-semibold underline underline-offset-2">
                            Conditions Générales de Vente
                          </Link>
                          {' '}de SOGNUDIMARE.{' '}
                          <span className="text-[#C4724F]">*</span>
                        </span>
                      </label>

                      <Button
                        type="submit"
                        className="w-full bg-[#C4724F] hover:bg-[#C4724F]/90 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        size="lg"
                        disabled={!selectedCruise || submitting || !cgvAccepted}
                        data-testid="submit-booking-btn"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>Creation...</span>
                        ) : (
                          <><Lock className="mr-2 h-4 w-4" />Procéder au paiement<ArrowRight className="ml-2 h-5 w-5" /></>
                        )}
                      </Button>
                      <div className="flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
                        <ShieldCheck className="h-4 w-4" /> Paiement sécurisé par Square
                      </div>
                      <div className="flex items-center justify-center gap-3 mt-2" data-testid="recap-payment-icons">
                        <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#1A1F71"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold" fontFamily="Arial, sans-serif">CB</text></svg>
                        <svg viewBox="0 0 48 32" className="h-5 w-auto" fill="none"><rect width="48" height="32" rx="4" fill="#fff" stroke="#e2e8f0"/><text x="24" y="21" textAnchor="middle" fill="#1A1F71" fontSize="13" fontWeight="bold" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg>
                        <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="9" fill="#EB001B"/><circle cx="29" cy="16" r="9" fill="#F79E1B"/><path d="M24 9.34a9 9 0 0 1 0 13.32 9 9 0 0 1 0-13.32z" fill="#FF5F00"/></svg>
                        <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#006FCF"/><text x="24" y="21" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial, sans-serif">AMEX</text></svg>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* STEP 2: Square Payment */}
      {step === 'payment' && (
        <section className="py-16 bg-[#FDFCFA]" data-testid="payment-step">
          <div className="max-w-xl mx-auto px-4">
            <Card className="border-2 border-[#2A9D8F]/20">
              <CardHeader className="bg-[#0A2540] text-white rounded-t-lg">
                <CardTitle className="text-lg flex items-center gap-2"><Lock className="h-5 w-5" /> Paiement sécurisé</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="p-4 bg-[#F5F3EF] rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4A5568]">Croisière</span>
                    <span className="font-medium text-[#0A2540]">{selectedCruise?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4A5568]">Total croisière</span>
                    <span className="font-medium text-[#0A2540]">{totals.total.toFixed(0)}€</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="font-semibold text-[#C4724F]">Acompte à payer maintenant</span>
                    <span className="text-2xl font-display font-bold text-[#C4724F]">{totals.deposit}€</span>
                  </div>
                </div>
                <div data-testid="square-payment-form">
                  <PaymentForm
                    applicationId={SQUARE_APP_ID}
                    locationId={SQUARE_LOCATION_ID}
                    cardTokenizeResponseReceived={handlePaymentToken}
                    createPaymentRequest={() => ({
                      countryCode: 'FR',
                      currencyCode: 'EUR',
                      total: { amount: String(totals.deposit), label: 'SOGNUDIMARE - Acompte croisière' },
                    })}
                  >
                    <CreditCard
                      buttonProps={{
                        css: {
                          backgroundColor: '#C4724F', color: '#fff', fontSize: '16px', fontWeight: '600',
                          padding: '12px', borderRadius: '8px', '&:hover': { backgroundColor: '#b5633f' },
                        },
                      }}
                      style={{
                        input: { fontSize: '16px', color: '#0A2540' },
                        'input::placeholder': { color: '#94A3B8' },
                        '.input-container': { borderColor: '#e2e8f0', borderRadius: '8px' },
                        '.input-container.is-focus': { borderColor: '#2A9D8F' },
                      }}
                    >
                      {paymentProcessing ? 'Traitement en cours...' : `Payer ${totals.deposit}€`}
                    </CreditCard>
                  </PaymentForm>
                </div>
                <div className="flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
                  <ShieldCheck className="h-4 w-4" /> Paiement 100% sécurisé
                </div>
                <div className="flex items-center justify-center gap-3" data-testid="payment-step-icons">
                  <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#1A1F71"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold" fontFamily="Arial, sans-serif">CB</text></svg>
                  <svg viewBox="0 0 48 32" className="h-5 w-auto" fill="none"><rect width="48" height="32" rx="4" fill="#fff" stroke="#e2e8f0"/><text x="24" y="21" textAnchor="middle" fill="#1A1F71" fontSize="13" fontWeight="bold" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg>
                  <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="9" fill="#EB001B"/><circle cx="29" cy="16" r="9" fill="#F79E1B"/><path d="M24 9.34a9 9 0 0 1 0 13.32 9 9 0 0 1 0-13.32z" fill="#FF5F00"/></svg>
                  <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#006FCF"/><text x="24" y="21" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial, sans-serif">AMEX</text></svg>
                </div>
                <Button type="button" variant="ghost" className="w-full text-[#94A3B8]" onClick={() => setStep('form')} data-testid="back-to-form-btn">
                  Retour au formulaire
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* FAQ */}
      {step === 'form' && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-bold text-[#0A2540] text-center mb-8">Questions frequentes</h2>
            <div className="space-y-4">
              {[
                { q: 'Comment fonctionne le paiement ?', a: 'Un acompte de 40% est demande a la réservation. Puis 30% du total est a regler 30 jours après le 1er paiement. Le solde restant est du 60 jours avant l\'embarquement.' },
                { q: 'Comment fonctionne la carte Club ?', a: 'La carte Club est individuelle : chaque passager doit avoir sa propre carte pour bénéficier de la remise. Par exemple, pour une cabine a 2, il faut 2 cartes.' },
                { q: 'Puis-je voyager seul en cabine ?', a: 'Oui ! L\'option "solo" vous permet de réserver une cabine complète pour vous seul avec une remise de 10% sur le prix de la cabine.' },
              ].map((faq, i) => (
                <div key={i} className="p-4 bg-[#F5F3EF] rounded-lg">
                  <h4 className="font-semibold text-[#0A2540] mb-2">{faq.q}</h4>
                  <p className="text-sm text-[#4A5568]">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ReservationPage;
